import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDistanceToNow } from 'date-fns';
import { getLocale } from './locale';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date string as UTC. Backend returns naive UTC timestamps without
 * a trailing "Z" (e.g. "2024-03-15T10:00:00"). Without the Z, JavaScript's
 * Date constructor treats these as local time, causing wrong results for
 * users in non-UTC timezones.
 */
function parseAsUTC(date: string | Date): Date {
  if (
    typeof date === 'string' &&
    date.includes('T') &&
    !date.endsWith('Z') &&
    !date.includes('+', 10) &&
    !date.includes('-', 10)
  ) {
    return new Date(date + 'Z');
  }
  return new Date(date);
}

// Date display options, formatted via Intl in the user's locale (see getLocale).
// en-US -> "Mar 15, 2024" / "Mar 15, 2024, 2:30 PM"
// en-GB -> "15 Mar 2024" / "15 Mar 2024, 14:30"
//
// `timeZone: 'UTC'` is pinned deliberately, not an oversight: every backend
// timestamp fed through these helpers is naive UTC (see parseAsUTC above), so
// the instant IS the UTC calendar day/time - that's what the data means.
// Without pinning, Intl.DateTimeFormat renders in the host's local timezone,
// which can roll the date to the wrong day for timestamps near midnight UTC
// (e.g. 23:30 UTC read as 00:30 the next day in Europe/London during BST).
// If a future call site genuinely wants local-timezone rendering (e.g. a
// personal "last login" shown to the end user in their own timezone), give it
// an explicit local variant/option rather than making this ambiguous.
const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
};
const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTIONS,
  hour: 'numeric',
  minute: '2-digit',
  // Pinning timeZone: 'UTC' above renders a clock time with no indication
  // it's UTC (regressing StatusPage/AdminContributorActivityPage from a
  // correct local instant to an unlabelled UTC one). Add the zone label
  // explicitly so it's never mistaken for the viewer's local time.
  timeZoneName: 'short',
};

export function formatDate(date: string | Date, locale: string = getLocale()): string {
  return new Intl.DateTimeFormat(locale, DATE_OPTIONS).format(parseAsUTC(date));
}

export function formatDateTime(date: string | Date, locale: string = getLocale()): string {
  return new Intl.DateTimeFormat(locale, DATE_TIME_OPTIONS).format(parseAsUTC(date));
}

export function formatRelativeTime(date: string | Date): string {
  return formatDistanceToNow(parseAsUTC(date), { addSuffix: true });
}

/** Whole (fractional) days between `date` (naive-UTC-aware) and now. Infinity if null/undefined. */
export function daysSince(date?: string | Date | null): number {
  if (!date) return Infinity;
  return (Date.now() - parseAsUTC(date).getTime()) / 86_400_000;
}

export function formatNumber(num: number, decimals = 0, locale: string = getLocale()): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

export function formatPercent(num: number, decimals = 1): string {
  return `${formatNumber(num, decimals)}%`;
}

export function formatDuration(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  } else if (hours < 24) {
    return `${formatNumber(hours, 1)} hrs`;
  } else {
    return `${formatNumber(hours / 24, 1)} days`;
  }
}

export function getTrendColor(trend: 'up' | 'down' | 'stable', isPositive: boolean): string {
  if (trend === 'stable') return 'text-gray-600';
  if ((trend === 'up' && isPositive) || (trend === 'down' && !isPositive)) {
    return 'text-green-600';
  }
  return 'text-red-600';
}

export function getTrendIcon(trend: 'up' | 'down' | 'stable'): string {
  if (trend === 'up') return '↑';
  if (trend === 'down') return '↓';
  return '→';
}

/**
 * Maps frontend time range presets to backend time period strings.
 *
 * @param days - Number of days (7, 30, 90, 180, 365)
 * @returns Backend time period string (daily, weekly, monthly, quarterly, biannual, yearly)
 */
export function getTimePeriodForBackend(days: string | number): 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'biannual' | 'yearly' {
  const daysNum = typeof days === 'string' ? parseInt(days, 10) : days;

  if (daysNum <= 1) return 'daily';
  if (daysNum <= 7) return 'weekly';
  if (daysNum <= 30) return 'monthly';
  if (daysNum <= 90) return 'quarterly';
  if (daysNum <= 180) return 'biannual';
  return 'yearly';
}

/**
 * Safe number formatting that handles null/undefined values.
 * Returns a fallback string (default '-') when value is not a valid number.
 */
export function safeFormatNumber(
  value: number | null | undefined,
  decimals: number = 0,
  fallback: string = '-',
  locale: string = getLocale()
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return fallback;
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Safe percentage formatting with null/undefined handling.
 */
export function safeFormatPercent(
  value: number | null | undefined,
  decimals: number = 1,
  fallback: string = '-'
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return fallback;
  }
  return `${safeFormatNumber(value, decimals)}%`;
}

/**
 * Safe duration formatting for hours with null/undefined handling.
 */
export function safeFormatDuration(
  hours: number | null | undefined,
  fallback: string = '-'
): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) {
    return fallback;
  }
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  } else if (hours < 24) {
    return `${safeFormatNumber(hours, 1)} hrs`;
  } else {
    return `${safeFormatNumber(hours / 24, 1)} days`;
  }
}

/**
 * Type guard for numeric values that are safe to format.
 */
export function isValidNumber(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value);
}
